"use client";

import { HeartIcon as HeartIconOutline } from "@heroicons/react/24/outline";
import {
  HeartIcon as HeartIconSolid,
  StarIcon,
} from "@heroicons/react/24/solid";
import { Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useClientAuth } from "~/hooks/use-auth";
import { calculateAverageRating, formatter } from "~/lib/utils";
import { api } from "~/trpc/react";
import { ProductProps } from "~/types/types";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";

export default function ProductListing({
  product,
  index,
}: {
  product: ProductProps;
  index: number;
}) {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);

  const router = useRouter();

  const { isAuth } = useClientAuth();

  const utils = api.useUtils();

  const { mutateAsync: postWishlist } = api.wishlists.postWishlist.useMutation({
    retry: false,
  });
  const { mutateAsync: deleteWishlist } =
    api.wishlists.deleteWishlist.useMutation({
      retry: false,
    });

  const { data } = api.wishlists.getWishlist.useQuery(undefined, {
    refetchOnWindowFocus: true,
    enabled: isAuth,
  });

  const checkWishlist = product?.id
    ? data?.wishlist?.some(({ id }) => id === product.id)
    : false;
  const averageRating = calculateAverageRating(product?.productReviews);

  const AddWishlistRemoveWishlist = () => {
    if (!isAuth) {
      return router.push("/sign-in");
    }

    setLoading(true);
    const action = checkWishlist ? deleteWishlist : postWishlist;
    const successMessage = checkWishlist
      ? "Item removed from wishlist."
      : "Item added to wishlist.";

    toast.promise(action({ id: product.id }), {
      loading: "Loading...",
      success: async () => {
        await utils.wishlists.getWishlist.invalidate();
        setLoading(false);
        return successMessage;
      },
      error: () => {
        setLoading(false);
        return "Something wen wrong.";
      },
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, index * 75);

    return () => clearTimeout(timer);
  }, [index]);

  if (!product || !isVisible) return <ProductPlaceholder />;
  const validUrls = product?.images?.[0]?.url ?? "";

  const percentage =
    product?.discount > 0 ? (product?.discount / product?.price) * 100 : 0;

  const Price = () => {
    if (product.discount > 0) {
      const priceDiscount = product.price - product.discount;
      // const percentage = (product.discount / product.price) * 100;

      return (
        <div className="flex items-center gap-2">
          {/* <Typography className="font-medium text-red-500">
                    {`-${Math.round(percentage)}%`}
              </Typography> */}
          <span className="font-bold">
            {formatter.format(Number(priceDiscount))}
          </span>
          <span className="text-sm font-medium text-muted-foreground line-through">
            {formatter.format(Number(product.price))}
          </span>
        </div>
      );
    }
  };

  if (isVisible && product) {
    return (
      <div className="group flex flex-col justify-between rounded-2xl border bg-background p-2">
        <div className="relative overflow-hidden rounded-2xl transition">
          <Link
            href={`/product/${product.slug}`}
            className="relative block aspect-square size-full"
          >
            <Image
              key={validUrls}
              src={validUrls}
              alt={`image`}
              fill
              className=""
              loading="eager"
            />
          </Link>
          {product?.discount > 0 && (
            <span className="!absolute left-1 top-2 rounded-full bg-red-500 px-2 py-1 text-xs font-normal text-white">
              {`-${Math.round(percentage)}%`}
            </span>
          )}
          <Button
            variant="secondary"
            size="icon"
            className="!absolute right-1 top-1 rounded-full"
            onClick={AddWishlistRemoveWishlist}
          >
            {isLoading ? (
              <Loader2 className="animate-spin" />
            ) : checkWishlist ? (
              <HeartIconSolid className="size-5 text-red-600" />
            ) : (
              <HeartIconOutline className="size-5" />
            )}
          </Button>
        </div>
        <div className="mb-1 mt-2 space-y-4 px-1">
          <div className="flex gap-2"></div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <h3 className="text-muted-foreground text-xs font-normal capitalize">
                {product.brand.title} {"|"} {product.category.title}
              </h3>
            </div>
            <Link
              href={`/product/${product?.slug}`}
              className="transition-all hover:text-muted-foreground"
            >
              <h2 className="line-clamp-2 text-base font-medium">
                {product.title}
              </h2>
            </Link>
          </div>
          <div className="flex items-center justify-between">
            {product.stock > 0 ? (
              <Price />
            ) : (
              <span className="font-medium text-red-500">
                Out of Stock
              </span>
            )}
            {product?.productReviews?.length > 0 && (
              <div className="text-muted-foreground flex items-center justify-center text-xs font-medium">
                <StarIcon className="mr-1 size-4 text-amber-500" />
                <h4>{`${averageRating.toFixed(1)} (${product?.productReviews?.length})`}</h4>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const ProductPlaceholder = () => {
  return (
    <Skeleton className="aspect-square rounded-xl" />
  );
};
